import {
    AfterViewInit,
    Component,
    computed,
    DestroyRef,
    effect,
    ElementRef,
    inject,
    Injector,
    input,
    OnDestroy,
    OnInit,
    output,
    signal,
    viewChild,
    ViewEncapsulation,
    ChangeDetectionStrategy,
} from "@angular/core"
import type {CmLogger} from "@cm/log"
import {CM_LOGGER_CONFIG} from "@common/services/logging"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"
import {Subject} from "rxjs"
import {buffer, debounceTime, delayWhen} from "rxjs/operators"
import {CustomLoadingScreenComponent} from "@app/common/components/progress/custom-loading-screen/custom-loading-screen.component"
import {ActionMenuComponent} from "@app/common/components/viewers/configurator/action-menu/action-menu.component"
import {ActionMenuService} from "@app/common/components/viewers/configurator/action-menu/services/action-menu.service"
import {ConfigMenuComponent} from "@app/common/components/viewers/configurator/config-menu/config-menu.component"
import {ConfigMenuService} from "@app/common/components/viewers/configurator/config-menu/services/config-menu.service"
import {CmOnboardingHintComponent} from "@app/common/components/viewers/configurator/configurator/onboarding-hint/onboarding-hint.component"
import {DebugMenuComponent} from "@app/common/components/viewers/configurator/debug-menu/debug-menu.component"
import {ExtensionContext} from "@app/common/components/viewers/configurator/extensions/configurator-api-extension"
import {increaseOnboardingCounter, isOnboardingRequired} from "@app/common/components/viewers/configurator/helpers/onboarding"
import {
    createMaterialReferenceFromLegacyId,
    decodeConfiguratorUrlParameters,
    ImageDataParameter,
    initializeTemplateParameterValue,
} from "@app/common/components/viewers/configurator/helpers/parameters"
import {ArService} from "@app/common/components/viewers/configurator/services/ar.service"
import type {CMConfiguratorElement, PdfDownloadOptions} from "@app/common/components/viewers/configurator/types/cm-configurator"
import {getParametersForArGeneration} from "@app/common/helpers/ar/ar"
import {
    exitFullscreen,
    fullscreenActive,
    inIframe,
    listenToFullscreenChange,
    openFullscreen,
    stopListeningToFullscreenChange,
} from "@app/common/helpers/fullscreen/fullscreen"
import {AuthService} from "@app/common/services/auth/auth.service"
import {FilesService} from "@app/common/services/files/files.service"
import {LocalStorageService} from "@app/common/services/local-storage/local-storage.service"
import {PdfGenerationService} from "@app/common/services/pdf-generation/pdf-generation.service"
import {PricingService} from "@app/pricing/services/pricing.service"
import {ThreeTemplateSceneProviderComponent} from "@app/template-editor/components/three-template-scene-provider/three-template-scene-provider.component"
import {AnnotationStyleRegistry} from "@app/template-editor/helpers/annotation-style-registry"
import {BakeTextureService} from "@app/template-editor/services/bake-texture.service"
import {MeshGeometryService} from "@app/template-editor/services/mesh-geometry.service"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {ThreeSceneManagerService} from "@app/template-editor/services/three-scene-manager.service"
import {MaterialGraph} from "@cm/material-nodes"
import {
    ConfiguratorParameter,
    ConfiguratorParameterType,
    Parameters,
    isBooleanInput,
    isMaterialInput,
    MaterialGraphReference,
    deserializeTemplateGraph,
} from "@cm/template-nodes"
import {extensionForContentType, TypedImageData} from "@cm/utils"
import {ConfiguratorDataGQL, PdfDataFragment} from "@common/components/viewers/configurator/configurator/configurator.generated"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {SceneCamera, ThreeTemplateSceneViewerComponent} from "@template-editor/components/three-template-scene-viewer/three-template-scene-viewer.component"
import {TemplateNodeClipboardService} from "@template-editor/services/template-node-clipboard.service"
import filenamify from "filenamify/browser"
import {toDataURL} from "qrcode"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-configurator",
    templateUrl: "./configurator.component.html",
    styleUrl: "./configurator.component.scss",
    imports: [
        ActionMenuComponent,
        CmOnboardingHintComponent,
        ConfigMenuComponent,
        CustomLoadingScreenComponent,
        ThreeTemplateSceneViewerComponent,
        ThreeTemplateSceneProviderComponent,
        DebugMenuComponent,
    ],
    encapsulation: ViewEncapsulation.ShadowDom,
    providers: [SceneManagerService, TemplateNodeClipboardService, MeshGeometryService, BakeTextureService],
    host: {
        "[attr.data-cm-loaded]": "$loaded() ? '' : null",
    },
})
export class ConfiguratorComponent implements OnInit, OnDestroy, AfterViewInit {
    /*Angular elements translates the input signals to html attributes, which are strings. Those can be set once
    on the component (e.g. as <cm-configurator-main parameters="...">. Note that configuratorElement.parameters="..."
    does not work.*/
    readonly $_useExternalMenu = input<string | boolean>("false", {alias: "useExternalMenu"})
    readonly $parameters = input<string | undefined>(undefined, {alias: "parameters"})
    readonly $_showUi = input<string | boolean>("true", {alias: "showUi"})
    readonly $_forceWebGl = input<string | boolean>("true", {alias: "forceWebGl"})
    readonly $templateUuid = input<string | undefined>(undefined, {alias: "templateUuid"})
    readonly $organizationUuid = input<string | undefined>(undefined, {alias: "organizationUuid"})
    readonly $articleId = input<string | undefined>(undefined, {alias: "articleId"})
    readonly $pixelRatio = input<number | undefined>(undefined, {alias: "pixelRatio"})
    readonly $fullscreenTarget = input<string | undefined>(undefined, {alias: "fullscreenTarget"})

    readonly loadingCompleted = output<void>()
    readonly changeCompleted = output<{
        id: string
    }>() //emitted after any parameter was changed from outer website, i.e. more often than configurationLoaded
    readonly configurationLoaded = output<void>()
    readonly arUrl = output<string | undefined>()
    readonly pdfDownloadTriggered = output<void>()

    readonly $loaded = signal(false)
    readonly $runningOperation = signal<"none" | "stl" | "gltf" | "ar" | "loading">("none")

    readonly localSceneManagerService = inject(SceneManagerService)
    readonly localMeshGeometryService = inject(MeshGeometryService)
    readonly configMenuService = inject(ConfigMenuService)
    readonly actionMenuService = inject(ActionMenuService)
    readonly arService = inject(ArService)
    readonly pricingService = inject(PricingService)
    readonly bakeTextureService = inject(BakeTextureService)

    private parameterChanges$ = new Subject<{id: string}>()
    readonly pdfGenerationService = inject(PdfGenerationService)
    readonly injector = inject(Injector)
    readonly localStorageService = inject(LocalStorageService)
    private readonly authService = inject(AuthService)
    readonly $showDebugMenu = computed(() => this.authService.$isStaff() || this.localStorageService.developmentMode)

    readonly configuratorDataGql = inject(ConfiguratorDataGQL)

    private readonly destroyRef = inject(DestroyRef)
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef)
    private readonly logger: CmLogger = inject(CM_LOGGER_CONFIG)
    private threeSceneManagerService: ThreeSceneManagerService | undefined
    private readonly $viewer = viewChild.required<ThreeTemplateSceneViewerComponent>("viewer")
    protected readonly $inFullscreen = signal<boolean>(false)

    readonly $previewImageData = signal<TypedImageData | null>(null)
    readonly $overlayPreviewImageData = signal<boolean>(false)

    readonly $configChangeOverlay = signal<string | null>(null)
    readonly $configChangeOverlayVisible = signal<boolean>(false)
    private configChangeOverlayCapturing = false
    /** Number of parameter changes currently syncing; the overlay only hides once they all resolve. */
    private configChangesInFlight = 0

    // TODO: this could be moved into the organisation in the future
    protected readonly customLoadingSpinnersForOrgs: Record<number, string> = {
        277: "https://storage.googleapis.com/cm-platform-prod-static/Delvaux/configurator/loading.svg",
    }
    protected readonly $customLoadingScreen = computed<{backgroundImageUrl?: string; loadingSpinnerSvgUrl?: string | null; showSpinner: boolean}>(() => {
        const organizationLegacyId = this.localSceneManagerService.$defaultCustomerId()
        const sceneOptions = this.localSceneManagerService.$sceneOptions()

        return {
            backgroundImageUrl: sceneOptions?.loadingBackgroundImage?.downloadUrl,
            loadingSpinnerSvgUrl: organizationLegacyId ? this.customLoadingSpinnersForOrgs[organizationLegacyId] : undefined,
            showSpinner: !!organizationLegacyId,
        }
    })

    readonly $useExternalMenu = computed<boolean>(() => {
        return this.$_useExternalMenu() === "true" || this.$_useExternalMenu() === true
    })

    readonly $showUi = computed<boolean>(() => {
        return this.$_showUi() === "true" || this.$_showUi() === true
    })

    readonly $forceWebGl = computed<boolean>(() => {
        if (this.localStorageService.developmentMode) {
            return false
        }
        return this.$_forceWebGl() === "true" || this.$_forceWebGl() === true
    })

    readonly $showMenu = computed<boolean>(() => {
        const sceneOptions = this.localSceneManagerService.$sceneOptions()
        const scenePropertiesHideMenu = sceneOptions?.uiStyle === "hidden"

        const fullscreenNeedsInternalMenu = this.$inFullscreen() && !this.$fullscreenTarget()
        return !scenePropertiesHideMenu && (fullscreenNeedsInternalMenu || !this.$useExternalMenu())
    })

    readonly $camera = computed<SceneCamera | undefined>(() => {
        const cameras = this.localSceneManagerService.$cameras()
        if (cameras.length > 0) return {parameters: cameras[0], transform: cameras[0].transform}
        return undefined
    })

    readonly $pdfEnabled = computed(() => {
        const sceneOptions = this.localSceneManagerService.$sceneOptions()
        // Once all legacy templates actively set this flag, we can default to the more sensible 'false'
        const enablePdfDownload = sceneOptions?.enablePdfDownload ?? true
        return enablePdfDownload && this.localSceneManagerService.$pdfSettings() !== undefined
    })

    readonly $onboardingClicked = signal<boolean>(false)

    readonly $onboardingHintVisible = computed(() => {
        const sceneOptions = this.localSceneManagerService.$sceneOptions()
        return !this.$onboardingClicked() && Boolean(sceneOptions?.enableOnboardingHint && isOnboardingRequired())
    })

    readonly $allowSelect = signal<boolean>(false)
    readonly $isAlive = signal(true)

    private $pdfData = signal<PdfDataFragment | undefined>(undefined)
    protected $organizationId = signal<string | undefined>(undefined)
    private destroyed = false

    constructor() {
        effect(() => {
            const sceneOptions = this.localSceneManagerService.$sceneOptions()

            this.configMenuService.setUiStyle(sceneOptions?.uiStyle ?? "default")
            this.configMenuService.setIconSize(sceneOptions?.iconSize ?? 24)
            this.configMenuService.setUseSectionedMenu(sceneOptions?.useSectionedMenu ?? true)
        })

        effect(() => {
            const templateUuid = this.$templateUuid()
            if (!templateUuid) return
            ;(async () => {
                const inputParams = this.$parameters()
                const parameters = inputParams ? (await decodeConfiguratorUrlParameters(inputParams, this.injector)).parameters : undefined

                this.loadConfiguratorData({templateId: templateUuid}, parameters)
            })()
        })

        effect(() => {
            const organizationId = this.$organizationUuid()
            const articleId = this.$articleId()
            if (!organizationId || !articleId) return
            ;(async () => {
                const inputParams = this.$parameters()
                const parameters = inputParams ? (await decodeConfiguratorUrlParameters(inputParams, this.injector)).parameters : undefined

                this.loadConfiguratorData({organizationId, articleId}, parameters)
            })()
        })
    }

    ngOnInit() {
        AnnotationStyleRegistry.getInstance().injectIntoShadowDom()

        // Collect all IDs during the debounce period, then process once and emit changeCompleted for each ID
        this.parameterChanges$
            .pipe(
                buffer(this.parameterChanges$.pipe(debounceTime(10))),
                delayWhen(() => this.processParameterChanges()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((changes) => {
                // Emit changeCompleted for each ID that was collected
                for (const {id} of changes) {
                    this.changeCompleted.emit({id})
                }
            })

        this.localSceneManagerService.descriptorList$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((descriptors) => {
            this.configMenuService.setInterface(descriptors)
        })

        this.configMenuService.configSelected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (config) => {
            await this.captureConfigChangeOverlay()
            this.localSceneManagerService.setTemplateParameter(config.config.props.id, config.variant.id)
            await this.syncChangesAndHideOverlay(true)
            this.configurationLoaded.emit()
            this.configMenuService.setSynchronizing(false)
        })

        this.configMenuService.materialSelected$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (material) => {
            await this.captureConfigChangeOverlay()
            const materialReference = await createMaterialReferenceFromLegacyId(material.legacyId, this.injector)
            this.localSceneManagerService.setTemplateParameter(material.input.props.id, materialReference)
            this.syncChangesAndHideOverlay(false)
        })

        this.configMenuService.valueChanged$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (value) => {
            // Only toggles are discrete; live number/string inputs would freeze the viewer behind a
            // stale snapshot on every keystroke, so skip the overlay for them.
            if (isBooleanInput(value.config)) await this.captureConfigChangeOverlay()
            this.localSceneManagerService.setTemplateParameter(value.config.props.id, value.value)
            this.syncChangesAndHideOverlay(false)
        })

        this.actionMenuService.toggleFullscreen$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.toggleFullscreen()
        })

        this.actionMenuService.creatingStl$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isCreating) => {
            this.creatingStl(isCreating)
        })

        this.actionMenuService.creatingGltf$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isCreating) => {
            this.creatingGltf(isCreating)
        })

        this.actionMenuService.toggleDimensionGuides$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.localSceneManagerService.$showDimensionGuides.set(!this.localSceneManagerService.$showDimensionGuides())
        })

        this.actionMenuService.captureSnapshot$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
            this.saveSnapshot("image/png")
        })

        this.arService.creatingAr$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isCreating) => {
            this.creatingAr(isCreating)
        })

        this.arService.desktopArUrl$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((url) => {
            this.arUrl.emit(url)
        })

        this.actionMenuService.pdfDownloadRequested$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async () => {
            await this.downloadPdf()
            this.actionMenuService.emitPdfDownloadFinished()
        })

        listenToFullscreenChange(this.toggleFullscreenButton.bind(this))
    }

    ngOnDestroy() {
        this.$isAlive.set(false)
        this.destroyed = true
        stopListeningToFullscreenChange(this.toggleFullscreenButton.bind(this))
    }

    bindWebComponentApi() {
        const nativeElement = this.elementRef.nativeElement as CMConfiguratorElement & {
            //Not part of the official api
            getExtensionContext: () => ExtensionContext
            setAllowSelect: (allow: boolean) => void
            loadWithUrlParameters(urlParameters: string): Promise<void>
            loadConfiguratorData(
                lookupArgs: {templateId?: string; sceneLegacyId?: number; organizationId?: string; articleId?: string},
                initialParameters: Parameters | undefined,
            ): Promise<void>
            clearParameters(): Promise<void>
        }
        nativeElement.saveSnapshot = (mimeType?: "image/png" | "image/jpeg", quality?: number, transparentBackground?: boolean, fileName?: string) =>
            this.saveSnapshot(mimeType ?? "image/jpeg", quality, transparentBackground, fileName)
        nativeElement.captureSnapshotInMemory = (mimeType?: "image/png" | "image/jpeg", quality?: number, transparentBackground?: boolean) =>
            this.captureSnapshotInMemory(mimeType ?? "image/jpeg", quality, transparentBackground)
        nativeElement.viewInAr = this.viewInAr.bind(this)
        nativeElement.zoomIn = this.zoomIn.bind(this)
        nativeElement.zoomOut = this.zoomOut.bind(this)
        nativeElement.resetCamera = this.resetCamera.bind(this)
        nativeElement.loadConfigurator = (templateUuid: string) => this.loadConfiguratorData({templateId: templateUuid}, undefined)
        nativeElement.loadConfiguratorByArticleId = (organizationUuid: string, articleId: string) =>
            this.loadConfiguratorData({organizationId: organizationUuid, articleId}, undefined)
        nativeElement.getPrices = this.pricingService.getPrices.bind(this.pricingService)
        nativeElement.getPriceScopes = this.pricingService.getPriceScopes.bind(this.pricingService)
        nativeElement.getSkus = this.pricingService.getSkus.bind(this.pricingService)
        nativeElement.generateQrCode = this.generateQrCode.bind(this)
        nativeElement.toggleFullscreen = this.toggleFullscreen.bind(this)
        nativeElement.toggleDimensionGuides = this.toggleDimensionGuides.bind(this)
        nativeElement.downloadPdf = (options?: PdfDownloadOptions) => this.downloadPdf(options)
        nativeElement.loadWithUrlParameters = this.loadWithUrlParameters.bind(this)
        nativeElement.loadConfiguratorData = this.loadConfiguratorData.bind(this)

        nativeElement.getParameterList = this.getParameterList.bind(this)
        nativeElement.setParameter = this.setParameter.bind(this)
        nativeElement.clearParameters = this.clearParameters.bind(this)
        nativeElement.getExtensionContext = this.getExtensionContext.bind(this)

        nativeElement.setAllowSelect = (allow: boolean) => this.$allowSelect.set(allow)
    }

    ngAfterViewInit(): void {
        this.bindWebComponentApi()
    }

    onInitializedThreeSceneManagerService(threeSceneManagerService: ThreeSceneManagerService | undefined) {
        if (threeSceneManagerService) {
            threeSceneManagerService.$ambientLight.set(false)
            threeSceneManagerService.$showGrid.set(false)
            threeSceneManagerService.$displayMode.set("configurator")

            this.bakeTextureService.threeSceneManagerService.set(threeSceneManagerService)
            threeSceneManagerService.requestedResize$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
                this.setViewportSize()
                this.localSceneManagerService.compileTemplate()
            })
        }

        this.threeSceneManagerService = threeSceneManagerService
        this.localMeshGeometryService.threeSceneManagerService.set(threeSceneManagerService)
    }

    getThreeSceneManagerService(): ThreeSceneManagerService {
        if (!this.threeSceneManagerService) throw new Error("ThreeSceneManagerService not initialized")
        return this.threeSceneManagerService
    }

    setViewportSize() {
        const parameters = this.localSceneManagerService.$instanceParameters()
        parameters.updateParameters({["$viewportSize"]: this.$viewer().getViewportSize()})
        this.localSceneManagerService.$instanceParameters.set(parameters)
    }

    async loadWithUrlParameters(urlParameters: string) {
        const decodedUrlParameters = await decodeConfiguratorUrlParameters(urlParameters, this.injector)
        if (decodedUrlParameters.templateId) {
            void this.loadConfiguratorData({templateId: decodedUrlParameters.templateId}, decodedUrlParameters.parameters)
        } else if (decodedUrlParameters.sceneId) {
            void this.loadConfiguratorData({sceneLegacyId: decodedUrlParameters.sceneId}, decodedUrlParameters.parameters)
        } else if (decodedUrlParameters.organizationUuid && decodedUrlParameters.articleId) {
            void this.loadConfiguratorData(
                {organizationId: decodedUrlParameters.organizationUuid, articleId: decodedUrlParameters.articleId},
                decodedUrlParameters.parameters,
            )
        }
    }

    async loadConfiguratorData(
        lookupArgs: {templateId?: string; sceneLegacyId?: number; organizationId?: string; articleId?: string},
        initialParameters: Parameters | undefined,
    ) {
        this.logger.debug("Loading configurator data", lookupArgs)
        this.$runningOperation.set("loading")

        const template = (await fetchThrowingErrors(this.configuratorDataGql)(lookupArgs)).configuratorData
        if (!template) throw new Error("Failed to load configurator template")

        const graph = deserializeTemplateGraph(template.latestRevision?.graph)

        this.localSceneManagerService.$templateGraph.set(graph)
        this.localSceneManagerService.$defaultCustomerId.set(template.organizationLegacyId)
        this.localSceneManagerService.$templateRevisionId.set(template.latestRevision?.id)
        this.$pdfData.set(template.pdfData)
        this.$organizationId.set(template.organizationId)

        if (initialParameters) this.localSceneManagerService.$instanceParameters.set(initialParameters)
        this.setViewportSize()
        this.localSceneManagerService.compileTemplate()
        await this.getThreeSceneManagerService().sync(false)

        this.$runningOperation.set("none")
        this.pricingService.setup(template.id, template.organizationId, this.localSceneManagerService)

        if (!this.destroyed) {
            this.loadingCompleted.emit()
            this.$loaded.set(true)
        }
    }

    toggleFullscreen() {
        const fullscreen = fullscreenActive()
        if (fullscreen) {
            exitFullscreen()
        } else {
            const targetSelector = this.$fullscreenTarget()
            const target = targetSelector ? document.querySelector(targetSelector) : null
            if (targetSelector && !target)
                this.logger.warn(`fullscreenTarget selector "${targetSelector}" matched no element; falling back to the configurator element`)
            const fullscreenElement = target ?? (inIframe() ? document.documentElement : this.elementRef.nativeElement)
            openFullscreen(fullscreenElement)
        }
    }

    toggleFullscreenButton() {
        const fullscreen = fullscreenActive()
        this.$inFullscreen.set(fullscreen)
    }

    creatingStl(isCreating: boolean) {
        this.$runningOperation.set(isCreating ? "stl" : "none")
    }

    creatingGltf(isCreating: boolean) {
        this.$runningOperation.set(isCreating ? "gltf" : "none")
    }

    creatingAr(isCreating: boolean) {
        this.$runningOperation.set(isCreating ? "ar" : "none")
    }

    getParameterList() {
        const result: ConfiguratorParameter[] = []
        for (const descriptor of this.localSceneManagerService.getDescriptors())
            if (descriptor.props.type === "input") result.push(descriptor.toConfiguratorParameter())
        return result
    }

    async setParameter(id: string, type: ConfiguratorParameterType, value: number | string | ImageDataParameter): Promise<void> {
        const parameterValue = await initializeTemplateParameterValue(type, value, this.injector)
        this.localSceneManagerService.setTemplateParameter(id, parameterValue, false)
        // Debounce the synchronization to prevent frequent changes when multiple parameters are set in sequence
        this.parameterChanges$.next({id})
    }

    async clearParameters(): Promise<void> {
        const inputParams = this.$parameters()
        const initialParameters = inputParams
            ? (await decodeConfiguratorUrlParameters(inputParams, this.injector)).parameters
            : new Parameters({parameters: {}})

        await this.captureConfigChangeOverlay()
        this.localSceneManagerService.$instanceParameters.set(initialParameters)
        this.configMenuService.resetMenu()
        this.localSceneManagerService.compileTemplate()
        await this.syncChangesAndHideOverlay(true)
    }

    async processParameterChanges(): Promise<void> {
        await this.captureConfigChangeOverlay()
        this.localSceneManagerService.compileTemplate()
        // Wait for optional tasks too so changeCompleted only fires once the result is fully loaded.
        await this.syncChangesAndHideOverlay(true)
    }

    setMaterialGraph(inputId: string, graph: MaterialGraph) {
        this.localSceneManagerService.setTemplateParameter(inputId, new MaterialGraphReference({parameters: {materialGraph: graph}}))
    }

    setMaterialGraphToFirstInput(graph: MaterialGraph) {
        const materialInput = this.localSceneManagerService.getDescriptors().find((descriptor) => isMaterialInput(descriptor))
        if (!materialInput) throw new Error("No material input found")
        this.setMaterialGraph(materialInput.props.id, graph)
    }

    async saveSnapshot(mimeType: "image/png" | "image/jpeg", quality?: number, transparentBackground = false, fileName?: string) {
        const snapshot = await this.$viewer().captureSnapshot(mimeType, quality ?? 0.95, transparentBackground)
        // A transparent snapshot is always a PNG (see captureSnapshot), so use the matching extension.
        const fileMimeType = transparentBackground ? "image/png" : mimeType
        const baseName = filenamify(fileName ?? "snapshot", {replacement: "_"}).trim() || "snapshot"
        FilesService.downloadFile(`${baseName}.${extensionForContentType(fileMimeType)}`, snapshot)
    }

    captureSnapshotInMemory(mimeType: "image/png" | "image/jpeg", quality?: number, transparentBackground = false): Promise<string> {
        return this.$viewer().captureSnapshot(mimeType, quality ?? 0.95, transparentBackground)
    }

    async downloadPdf(options?: PdfDownloadOptions): Promise<void> {
        this.pdfDownloadTriggered.emit()
        const pdfData = this.$pdfData()
        if (!pdfData) throw new Error("PDF data not loaded.")
        const templateRevisionId = this.localSceneManagerService.$templateRevisionId()
        if (!templateRevisionId) throw new Error("Template revision id not set.")
        const pdfSettings = this.localSceneManagerService.$pdfSettings()
        if (!pdfSettings) throw new Error("PDF settings not set.")
        const jpgImageBytes = await this.$viewer().captureSnapshot("image/jpeg", 95)
        await this.pdfGenerationService.generatedAndDownloadPdf(jpgImageBytes, pdfData, pdfSettings, options)
    }

    viewInAr() {
        const templateRevisionId = this.localSceneManagerService.$templateRevisionId()
        if (!templateRevisionId) return
        this.arService.viewArModel(
            templateRevisionId,
            getParametersForArGeneration(this.localSceneManagerService),
            this.localSceneManagerService.$verticalArPlacement(),
        )
    }

    zoomIn(amount: number): void {
        this.$viewer().zoomIn(amount)
    }

    zoomOut(amount: number): void {
        this.$viewer().zoomOut(amount)
    }

    resetCamera(): void {
        this.$viewer().resetCamera()
    }

    /** Applies the post-change sync for any parameter change and hides the overlay once the configurator
     * is interactive again (critical tasks done). The overlay only hides after every overlapping change
     * has settled, so a fast change can't tear it down while a slower one is still loading. When
     * `waitForOptionalTasks` is set it additionally awaits optional tasks (e.g. higher-res textures), so
     * callers can sequence work that depends on the fully-loaded result. */
    private async syncChangesAndHideOverlay(waitForOptionalTasks: boolean): Promise<void> {
        this.configChangesInFlight++
        try {
            await this.getThreeSceneManagerService().sync(false)
        } finally {
            this.configChangesInFlight--
            if (this.configChangesInFlight === 0) this.hideConfigChangeOverlay()
        }
        if (waitForOptionalTasks) await this.getThreeSceneManagerService().sync(true)
    }

    /** Captures the current rendered frame and shows it as an overlay so the intermediate loading
     * states stay hidden while a parameter change is applied. Must be called *before* the change is
     * applied, while the viewer still shows the previous state. No-op if an overlay is already shown
     * or being captured (guards against rapid/batched changes). */
    private async captureConfigChangeOverlay(): Promise<void> {
        if (this.$configChangeOverlayVisible() || this.configChangeOverlayCapturing) return
        this.configChangeOverlayCapturing = true
        try {
            const snapshot = await this.$viewer().captureSnapshot("image/jpeg", 0.85)
            this.showConfigChangeOverlay(snapshot)
            // Wait for the overlay to actually paint before the scene starts updating.
            await new Promise<void>((resolve) => requestAnimationFrame(() => requestAnimationFrame(() => resolve())))
        } catch (error) {
            this.logger.debug("Failed to capture config change overlay snapshot", error)
        } finally {
            this.configChangeOverlayCapturing = false
        }
    }

    private showConfigChangeOverlay(snapshot: string) {
        this.$configChangeOverlay.set(snapshot)
        this.$configChangeOverlayVisible.set(true)
    }

    private hideConfigChangeOverlay() {
        this.$configChangeOverlayVisible.set(false)
    }

    onConfigChangeOverlayTransitionEnd(event: TransitionEvent) {
        if (event.target !== event.currentTarget || event.propertyName !== "opacity") return
        if (!this.$configChangeOverlayVisible()) this.$configChangeOverlay.set(null)
    }

    hideOnboardingIcons() {
        this.$onboardingClicked.set(true)
        increaseOnboardingCounter()
    }

    generateQrCode(url: string, errorCorrectionLevel: "high" | "low", width: number, margin: number): Promise<string> {
        return toDataURL(url, {errorCorrectionLevel, width, margin})
    }

    toggleDimensionGuides() {
        this.actionMenuService.toggleDimensionGuides()
    }

    getExtensionContext(): ExtensionContext {
        if (!this.threeSceneManagerService) throw new Error("ThreeSceneManagerService not initialized")
        return {
            sceneManagerService: this.localSceneManagerService,
            threeSceneManagerService: this.threeSceneManagerService,
            injector: this.injector,
            logger: this.logger,
            getOrganizationId: () => this.$organizationId(),
            getViewCamera: () => this.$viewer().$viewCamera(),
            setViewCamera: (transform, target) => this.$viewer().setViewCamera(transform, target),
        }
    }
}
